import { memo, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import {
  AnalyticsConstants,
  AnalyticsHooks,
  AnalyticsStyles,
  AnalyticsUtils,
  DemoChip,
  OverviewInsights,
} from '@frontend/analytics-shared';
import { Chart, XAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { weaveStatusColors, weaveStatusLabels } from '../constants';
import { useFetchAggregatedAppointmentsData } from '../hooks/use-fetch-aggregated-appointments-data';
import { useIsAppointmentDemoAccount } from '../hooks/use-is-appointment-demo-account';

type OverviewCardProps = {
  filters: AnalyticsCommonTypes.AnalyticsSummaryFilters;
};

export const AppointmentStatusesOverviewCard = memo(({ filters }: OverviewCardProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const isDemoAccount = useIsAppointmentDemoAccount();
  const { noLocationsHaveAclAccess } = AnalyticsHooks.useHasAclAccess({
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });
  const { isLoadingLocations, locationIds } = AnalyticsHooks.useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });

  const { data, isLoading } = useFetchAggregatedAppointmentsData({
    customFilters: {
      EndDate: AnalyticsUtils.appendCurrentTimeZone(filters.endDate.format('YYYY-MM-DDT23:59:59.999')),
      LocationID: locationIds || [],
      StartDate: AnalyticsUtils.appendCurrentTimeZone(filters.startDate.format('YYYY-MM-DDT00:00:00.000')),
      TimeZone: filters.timeZone,
    },
  });

  const formattedData = useMemo(() => {
    let total = 0;

    const values = Object.values(data.weaveStatusCounts).reduce<Record<string, number>>((acc, value) => {
      Object.entries(value).forEach(([key, count = 0]) => {
        acc[key] = (acc[key] || 0) + count;
        total += count;
      });

      return acc;
    }, {});

    return {
      chartData: {
        groups: Object.keys(values)
          .sort()
          .map((key) => ({
            name: key,
            values: {
              [key]: values[key],
            },
          })),
      },
      total,
      values,
    };
  }, [data]);

  return (
    <Chart
      bottomContentRenderer={<OverviewInsights title={t('Total Appointments')} value={formattedData.total} />}
      colors={weaveStatusColors}
      css={AnalyticsStyles.dashboardChartStyle}
      isLoading={isLoading || isLoadingLocations}
      labels={weaveStatusLabels}
    >
      <Chart.Header
        actions={[
          {
            label: t('Show More'),
            omit: noLocationsHaveAclAccess,
            onClick: () => {
              navigate({ to: '/analytics/appointment', search });
            },
            trackingId: 'show-more-appointment-analytics',
          },
        ]}
        leftElement={isDemoAccount && <DemoChip />}
        title={t('Appointment Status')}
      />
      <Chart.BarChart
        appearance={{
          customXAxisTick: ({ labels, groupName, ...rest }) => {
            return (
              <XAxisLabelValueTick
                {...rest}
                color={weaveStatusColors[groupName]}
                label={labels?.[groupName] || groupName}
                labelColor={theme.colors.neutral60}
                value={formatters.value(formattedData.values[groupName])}
              />
            );
          },
          height: AnalyticsConstants.DASHBOARD_CHART_SIZE,
          hideTooltipTitle: true,
          margin: { bottom: 20 },
          showXAxis: true,
        }}
        data={formattedData.chartData}
        formatValue={formatters.value}
      />
    </Chart>
  );
});

AppointmentStatusesOverviewCard.displayName = 'AppointmentStatusesOverviewCard';
